import { useEffect, useRef } from 'react'
import { usePageDataLog } from 'lib/hooks/usePageDataLog'
import { useFilterStore } from 'lib/hooks/useFilterStore'
import { useAppStore } from 'lib/hooks/useAppStore'
import { gqlCartFragment } from 'lib/sanity.queries'
import Script from 'next/script'
import {
  CartProvider,
  ShopifyProvider,
  useShopifyCookies,
  getClientBrowserParameters,
  sendShopifyAnalytics,
  AnalyticsEventName,
} from '@shopify/hydrogen-react'

import SiteLayout from 'components/SiteLayout'
import { Transition } from 'components/Transition'
import { AccountOverlay } from 'components/Account/AccountOverlay'
import { AnimatePresence, LazyMotion, domAnimation } from 'motion/react'
import { NotifyOverlay } from 'components/Product/NotifyOverlay'
import { CartOverlay } from 'components/Global/Cart/CartOverlay'

import '../styles/globals.css'

const sendPageView = (data) => {
  const payload = {
    ...getClientBrowserParameters(),
    ...data,
  }

  sendShopifyAnalytics({
    eventName: AnalyticsEventName.PAGE_VIEW,
    payload,
  })
}

const analyticsShopData = {
  shopId: 'gid://shopify/Shop/23806877',
  currency: 'USD',
  acceptedLanguage: 'en',
}

function MyApp({ Component, pageProps, router }) {
  const { accountModal, cartState } = useAppStore()
  const lastRoute = useRef(router.asPath)
  const lastScroll = useRef(0)
  useShopifyCookies({ hasUserConsent: true })
  const { resetFilters } = useFilterStore()

  usePageDataLog()

  const analytics = {
    hasUserConsent: true,
    ...analyticsShopData,
    ...pageProps.analytics,
  }
  const pagePropsWithAppAnalytics = {
    ...pageProps,
    analytics,
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.history.scrollRestoration = 'manual'
    let pixel

    const handleRouteChange = () => {
      resetFilters()
      const currentRoute = router.asPath
      const last = lastRoute.current
      pixel?.pageView()
      sendPageView(analytics)

      if (
        last.includes('/products/') &&
        currentRoute.includes('/collections')
      ) {
        const pos = lastScroll.current
        setTimeout(() => {
          window.scrollTo({
            top: pos,
            left: 0,
            behavior: 'instant' as any,
          })
        }, 500)
      } else {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant' as any,
          })
        }, 500)
      }

      lastRoute.current = currentRoute
      lastScroll.current = window.scrollY
    }

    sendPageView(analytics)

    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        pixel = ReactPixel
        ReactPixel.init(
          process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID ?? '1989744304613630',
          undefined,
          { autoConfig: true, debug: false }
        )

        router.events.on('routeChangeComplete', handleRouteChange)
      })

    const setVH = () => {
      document.documentElement.style.setProperty(
        '--vh',
        window.innerHeight * 0.01 + 'px'
      )
    }

    setVH()

    window.addEventListener('resize', setVH)
    return () => {
      window.removeEventListener('resize', setVH)
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [])

  useEffect(() => {
    // add class "scroll-hidden" to body
    if (cartState === 'open') {
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
    }
  }, [cartState])

  return (
    <main className={`bg-lightGray text-black`}>
      <LazyMotion features={domAnimation}>
        {!router.pathname.includes('studio') ? (
          <ShopifyProvider
            languageIsoCode="EN"
            countryIsoCode="US"
            storeDomain="https://chnge-nyc.myshopify.com"
            storefrontId="chnge-nyc"
            storefrontToken={
              process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN ?? ''
            }
            storefrontApiVersion="2023-10"
          >
            <CartProvider cartFragment={gqlCartFragment}>
              <AnimatePresence>
                {accountModal ? <AccountOverlay key="account-overlay" /> : null}
                <NotifyOverlay />
                {cartState === 'open' ? (
                  <CartOverlay
                    defaultCollection={
                      pageProps.settings?.recommendedCollection
                    }
                  />
                ) : null}
              </AnimatePresence>
              <SiteLayout preview={false} settings={pageProps.settings}>
                <Transition
                  component={Component}
                  pageProps={pagePropsWithAppAnalytics}
                  path={router.asPath}
                />
              </SiteLayout>
            </CartProvider>
          </ShopifyProvider>
        ) : (
          <Component {...pageProps} />
        )}
      </LazyMotion>
      <Script
        src="https://cdn.attn.tv/chnge/dtag.js"
        strategy="afterInteractive"
      />
    </main>
  )
}

export default MyApp
